import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';

/**
 * Feature 102 slice 4 — referral-feedback metrics for the admin dashboard.
 * Endpoint takes no filters (all-time view), so the key is static.
 */
export const useReferralFeedbackData = (enabled = true) =>
  useQuery({
    queryKey: ['analytics', 'referral-feedback'] as const,
    queryFn: () => analyticsApi.getReferralFeedback(),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

/**
 * Per-referral drill-down for one SP. Lazy: only fetched once the provider's
 * row is expanded (`enabled`), then cached per spUserId.
 */
export const useReferralFeedbackProviderDetail = (spUserId: string, enabled = true) =>
  useQuery({
    queryKey: ['analytics', 'referral-feedback', 'provider', spUserId] as const,
    queryFn: () => analyticsApi.getReferralFeedbackProviderDetail(spUserId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

/**
 * Care-seeker answers for one SP (CS table drill-down). Lazy: fetched once the
 * row is expanded, then cached per spUserId.
 */
export const useReferralFeedbackCsDetail = (spUserId: string, enabled = true) =>
  useQuery({
    queryKey: ['analytics', 'referral-feedback', 'cs-detail', spUserId] as const,
    queryFn: () => analyticsApi.getReferralFeedbackCsDetail(spUserId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

/**
 * Feedback attached to one engagement-log row, keyed by its source record.
 * Lazy: fetched only when the log row's feedback panel is expanded.
 */
export const useReferralSubjectFeedback = (
  subjectType: string,
  subjectId: string,
  enabled = true,
) =>
  useQuery({
    queryKey: ['analytics', 'referral-feedback', 'subject', subjectType, subjectId] as const,
    queryFn: () => analyticsApi.getReferralSubjectFeedback(subjectType, subjectId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });
